import React, { useEffect, useState } from 'react';
import { Circle as LeafletCircle, CircleMarker, MapContainer, Marker, Polyline, Popup, TileLayer, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { format } from 'date-fns';
import { Activity, Camera, Clock, ExternalLink, Navigation } from 'lucide-react';
import { geofenceService, positionService } from '../services/api';
import { useVehicleStore } from '../store';
import VehicleTypeIcon, { getVehicleTypeLabel, normalizeVehicleType } from './VehicleTypeIcon';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const vehicleMarkerImages = {
  car: '/images/vehicle-icons/car.png',
  motorcycle: '/images/vehicle-icons/motorcycle.png',
  truck: '/images/vehicle-icons/truck.png',
  van: '/images/vehicle-icons/van.png',
  bus: '/images/vehicle-icons/bus.png',
  other: '/images/vehicle-icons/tractor.png',
};

const createVehicleIcon = (vehicle, color = '#00b7ff', isMoving = false) => {
  const type = normalizeVehicleType(vehicle);
  const imageUrl = vehicleMarkerImages[type] || vehicleMarkerImages.other;
  const markerHtml = `
    <div class="rcrastro-vehicle-marker" style="--marker-color:${color}">
      ${isMoving ? '<span class="rcrastro-vehicle-marker__pulse"></span>' : ''}
      <span class="rcrastro-vehicle-marker__halo"></span>
      <span class="rcrastro-vehicle-marker__image-shell">
        <img src="${imageUrl}" alt="" class="rcrastro-vehicle-marker__image" />
      </span>
    </div>
  `;

  return L.divIcon({
    html: markerHtml,
    className: 'custom-marker',
    iconSize: [74, 64],
    iconAnchor: [37, 52],
    popupAnchor: [0, -48],
  });
};

const MapController = ({ center, layoutVersion }) => {
  const map = useMap();

  useEffect(() => {
    if (center) {
      map.setView(center, 15);
    }
  }, [center, map]);

  useEffect(() => {
    const resizeMap = () => map.invalidateSize();
    const timeout = setTimeout(resizeMap, 250);
    resizeMap();

    return () => clearTimeout(timeout);
  }, [layoutVersion, map]);

  return null;
};

const parseGeofenceCenter = (geofence) => {
  const coordinates = geofence?.coordinates || {};
  const raw = coordinates.center || coordinates;
  const latitude = Number(raw.latitude ?? raw.lat);
  const longitude = Number(raw.longitude ?? raw.lng ?? raw.lon);

  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
    return null;
  }

  return { latitude, longitude };
};

const isGenericAddress = (address) => {
  if (!address) return true;

  const normalized = String(address).trim().toLowerCase();
  return [
    'rastreador j16/gt06',
    'posicao simulada pelo painel',
    'posição simulada pelo painel',
    'celular autorizado pelo cliente',
  ].includes(normalized);
};

const buildStreetViewUrl = (latitude, longitude) => (
  `https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${latitude},${longitude}`
);

const buildStreetViewEmbedUrl = (latitude, longitude) => (
  `https://maps.google.com/maps?q=&layer=c&cbll=${latitude},${longitude}&cbp=11,0,0,0,0&output=svembed`
);

const buildMapsUrl = (latitude, longitude) => (
  `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`
);

const getTrailItems = (positions = []) => (
  positions
    .map((position) => {
      const latitude = Number(position.latitude);
      const longitude = Number(position.longitude);

      if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
        return null;
      }

      return {
        position,
        point: [latitude, longitude],
      };
    })
    .filter(Boolean)
);

const VehiclePopupContent = ({ vehicle, position, enabled }) => {
  const latitude = Number(position.latitude);
  const longitude = Number(position.longitude);
  const fallbackAddress = isGenericAddress(position.address) ? '' : position.address;
  const [address, setAddress] = useState(fallbackAddress);
  const [loadingAddress, setLoadingAddress] = useState(!fallbackAddress);
  const [links, setLinks] = useState({
    streetViewUrl: buildStreetViewUrl(latitude, longitude),
    streetViewEmbedUrl: buildStreetViewEmbedUrl(latitude, longitude),
    mapsUrl: buildMapsUrl(latitude, longitude),
  });

  useEffect(() => {
    if (!enabled || !Number.isFinite(latitude) || !Number.isFinite(longitude)) {
      return undefined;
    }

    let cancelled = false;
    setLoadingAddress(true);

    positionService.reverseGeocode({
      lat: latitude,
      lng: longitude,
      positionId: position.id,
    })
      .then((response) => {
        if (cancelled) return;

        const data = response.data.data || {};
        setAddress(data.address || data.display_name || fallbackAddress || 'Endereco nao encontrado');
        setLinks({
          streetViewUrl: data.streetViewUrl || buildStreetViewUrl(latitude, longitude),
          streetViewEmbedUrl: buildStreetViewEmbedUrl(latitude, longitude),
          mapsUrl: data.mapsUrl || buildMapsUrl(latitude, longitude),
        });
      })
      .catch(() => {
        if (cancelled) return;
        setAddress(fallbackAddress || 'Endereco indisponivel no momento');
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingAddress(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, latitude, longitude, position.id, fallbackAddress]);

  return (
    <div className="p-2 min-w-[260px] max-w-[300px]">
      <h3 className="font-bold text-lg mb-2">{vehicle.name}</h3>

      <div className="space-y-2 text-sm">
        <div className="flex items-center gap-2">
          <VehicleTypeIcon vehicle={vehicle} className="w-4 h-4" />
          <span>{vehicle.plate} - {getVehicleTypeLabel(vehicle)}</span>
        </div>

        {position.speed !== null && position.speed !== undefined && (
          <div className="flex items-center gap-2">
            <Activity className="w-4 h-4" />
            <span>{Number(position.speed).toFixed(1)} km/h</span>
          </div>
        )}

        {position.device_time && (
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4" />
            <span>{format(new Date(position.device_time), 'dd/MM/yyyy HH:mm')}</span>
          </div>
        )}

        <div className="flex items-start gap-2 mt-2 pt-2 border-t border-dark-600">
          <Navigation className="w-4 h-4 mt-0.5" />
          <span className="text-xs">
            {loadingAddress ? 'Buscando endereco...' : address}
          </span>
        </div>

        {enabled && (
          <div className="mt-3 overflow-hidden rounded-lg border border-dark-700 bg-dark-900">
            <div className="flex items-center gap-2 border-b border-dark-700 px-3 py-2 text-xs font-semibold text-dark-200">
              <Camera className="h-4 w-4" />
              Foto do local
            </div>
            <iframe
              title={`Foto do local - ${vehicle.name}`}
              src={links.streetViewEmbedUrl}
              className="h-32 w-full border-0"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        )}

        <div className="flex flex-wrap gap-2 pt-1">
          <a
            href={links.streetViewUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 rounded-lg bg-primary-600 px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-primary-500"
          >
            <Camera className="h-3.5 w-3.5" />
            Foto maior
          </a>
          <a
            href={links.mapsUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 rounded-lg bg-dark-700 px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-dark-600"
          >
            <ExternalLink className="h-3.5 w-3.5" />
            Abrir mapa
          </a>
        </div>

        <div className="flex gap-2 pt-1">
          <span className={`badge ${vehicle.is_blocked ? 'badge-danger' : vehicle.positions[0]?.motion ? 'badge-success' : 'badge-warning'}`}>
            {vehicle.is_blocked ? 'Bloqueado' : vehicle.positions[0]?.motion ? 'Em movimento' : 'Parado'}
          </span>

          {vehicle.positions[0]?.ignition && (
            <span className="badge badge-info">
              Ligado
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

const MapView = ({
  className = 'h-[600px]',
  layoutVersion = 0,
  trailPositions = [],
  showTrail = false,
}) => {
  const { vehicles, selectedVehicle } = useVehicleStore();
  const [center, setCenter] = useState([-23.5505, -46.6333]);
  const [zoom] = useState(12);
  const [geofences, setGeofences] = useState([]);
  const [activePopupKey, setActivePopupKey] = useState(null);
  const trailItems = getTrailItems(trailPositions);
  const trailPoints = trailItems.map((item) => item.point);
  const trailMarkerStep = Math.max(1, Math.ceil(trailItems.length / 18));

  useEffect(() => {
    const loadGeofences = async () => {
      try {
        const response = await geofenceService.getAll();
        setGeofences(response.data.data || []);
      } catch (error) {
        setGeofences([]);
      }
    };

    loadGeofences();
  }, []);

  useEffect(() => {
    if (selectedVehicle && selectedVehicle.positions?.[0]) {
      const pos = selectedVehicle.positions[0];
      setCenter([parseFloat(pos.latitude), parseFloat(pos.longitude)]);
    } else if (vehicles.length > 0) {
      const vehicleWithPos = vehicles.find((vehicle) => vehicle.positions?.[0]);

      if (vehicleWithPos) {
        const pos = vehicleWithPos.positions[0];
        setCenter([parseFloat(pos.latitude), parseFloat(pos.longitude)]);
      }
    }
  }, [selectedVehicle, vehicles]);

  const getVehicleColor = (vehicle) => {
    if (vehicle.is_blocked) return '#ef4444';
    if (!vehicle.is_active) return '#64748b';
    if (vehicle.positions?.[0]?.motion) return '#00e5ff';
    return '#0ea5e9';
  };

  return (
    <div className={`card p-0 overflow-hidden ${className}`}>
      <MapContainer
        center={center}
        zoom={zoom}
        className="h-full w-full rounded-xl"
        zoomControl={true}
      >
        <MapController
          center={selectedVehicle && selectedVehicle.positions?.[0] ? [
            parseFloat(selectedVehicle.positions[0].latitude),
            parseFloat(selectedVehicle.positions[0].longitude),
          ] : null}
          layoutVersion={layoutVersion}
        />

        <TileLayer
            attribution='&copy; <a href="https://www.maptiler.com/copyright/">MapTiler</a> &copy; OpenStreetMap contributors'
  url="https://api.maptiler.com/maps/streets/{z}/{x}/{y}.png?key=mzHo04n3BNJX0JEwAlCJ"
  tileSize={512}
  zoomOffset={-1}
  maxZoom={22}
  crossOrigin={true}
        />

        {geofences.filter((geofence) => geofence.is_active).map((geofence) => {
          const geofenceCenter = parseGeofenceCenter(geofence);
          if (!geofenceCenter) return null;

          return (
            <LeafletCircle
              key={geofence.id}
              center={[geofenceCenter.latitude, geofenceCenter.longitude]}
              radius={Number(geofence.radius || 0)}
              pathOptions={{
                color: geofence.color || '#0ea5e9',
               fillColor: geofence.color || '#0ea5e9',
                fillOpacity: 0.12,
                weight: 2,
              }}
            >
              <Popup>
                <div className="p-2 min-w-[180px]">
                  <h3 className="font-bold text-lg mb-1">{geofence.name}</h3>
                  <p className="text-sm">{Number(geofence.radius || 0)} metros</p>
                </div>
              </Popup>
            </LeafletCircle>
          );
        })}

        {showTrail && trailPoints.length > 1 && (
          <>
            <Polyline
              positions={trailPoints}
              pathOptions={{
                color: '#0f172a',
                weight: 9,
                opacity: 0.22,
                lineCap: 'round',
                lineJoin: 'round',
              }}
            />
            <Polyline
              positions={trailPoints}
              pathOptions={{
                color: '#00e5ff',
                weight: 5,
                opacity: 0.88,
                lineCap: 'round',
                lineJoin: 'round',
              }}
            />
            {trailItems.map((item, index) => {
              const isLast = index === trailItems.length - 1;
              const isFirst = index === 0;

              if (!isFirst && !isLast && index % trailMarkerStep !== 0) {
                return null;
              }

              return (
                <CircleMarker
                  key={`${item.position.id || item.position.device_time || index}-trail`}
                  center={item.point}
                  radius={isLast ? 6 : 4}
                  pathOptions={{
                    color: isLast ? '#22c55e' : '#00b7ff',
                    fillColor: isLast ? '#22c55e' : '#00e5ff',
                    fillOpacity: isLast ? 0.95 : 0.72,
                    weight: 2,
                  }}
                >
                  <Popup>
                    <div className="p-2 text-sm">
                      <div className="font-semibold">{isFirst ? 'Inicio do rastro' : isLast ? 'Ultimo ponto' : 'Ponto do rastro'}</div>
                      {item.position.device_time && (
                        <div>{format(new Date(item.position.device_time), 'dd/MM/yyyy HH:mm')}</div>
                      )}
                      <div>{Number(item.position.speed || 0).toFixed(0)} km/h</div>
                    </div>
                  </Popup>
                </CircleMarker>
              );
            })}
          </>
        )}

        {vehicles.map((vehicle) => {
          if (!vehicle.positions || vehicle.positions.length === 0) return null;

          const position = vehicle.positions[0];
          const lat = parseFloat(position.latitude);
          const lng = parseFloat(position.longitude);
          const popupKey = `${vehicle.id}-${position.id || position.device_time || `${lat}-${lng}`}`;

          if (Number.isNaN(lat) || Number.isNaN(lng)) return null;

          return (
            <Marker
              key={vehicle.id}
              position={[lat, lng]}
              icon={createVehicleIcon(vehicle, getVehicleColor(vehicle), position.motion)}
              eventHandlers={{
                click: () => setActivePopupKey(popupKey),
              }}
            >
              <Popup eventHandlers={{ remove: () => setActivePopupKey(null) }}>
                <VehiclePopupContent
                  vehicle={vehicle}
                  position={position}
                  enabled={activePopupKey === popupKey}
                />
              </Popup>
            </Marker>
          );
        })}
      </MapContainer>
    </div>
  );
};

export default MapView;
